import { Building2, ExternalLink, FileSearch, Link2, Network, RefreshCw, Search, Settings2, ShieldCheck, Sparkles } from "lucide-react";
import { useState } from "react";
import MarkdownText from "../../components/content/MarkdownText";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/Feedback";
import { Tabs } from "../../components/ui/Tabs";
import { PROCESSING_STATUSES } from "../../lib/statuses";
import {
  mergeResearchSources, RESEARCH_STAGE_LABELS, SOURCE_CATEGORY_LABELS, splitResearchReport, TRUST_LEVEL_LABELS,
} from "../../lib/research";
import CrmSopPanel from "./CrmSopPanel";
import ResearchBusinessBrief from "./ResearchBusinessBrief";
import ResearchVerdict from "./ResearchVerdict";

const SOURCE_PREVIEW_LIMIT = 8;
const CLAIM_LABELS = {
  company_name: "公司名称", company_domain: "公司域名", company_country: "公司所在国家",
  contact_name: "联系人姓名", contact_role: "联系人职位", contact_email: "联系人邮箱", contact_phone: "联系人电话",
  registration_number: "注册号", registration_status: "注册状态",
  business_scope: "经营范围", employee_count: "员工规模", revenue: "营收规模",
  trade_history: "贸易记录", import_record: "进口记录", shipment_record: "出货记录",
  project_name: "项目名称", project_capacity: "项目容量", company_project_relation: "公司与项目关系",
};

export default function AiResearchPanel({
  inquiry, onGenerate, onOpenTemplates, busy,
  onOpenRating,
  researchDetail, secondaryLoading, secondaryError,
}) {
  const [tab, setTab] = useState("brief");
  const [tabInquiryId, setTabInquiryId] = useState(inquiry.id);
  if (tabInquiryId !== inquiry.id) {
    setTabInquiryId(inquiry.id);
    setTab("brief");
  }
  const working = PROCESSING_STATUSES.has(inquiry.status) || busy;
  const sources = mergeResearchSources(
    inquiry.ai_research,
    researchDetail?.sources,
    inquiry.research_sources || researchDetail?.research_sources,
  );
  const report = splitResearchReport(inquiry.ai_research);
  const hasProcess = Boolean(researchDetail?.plan?.tasks?.length);
  const tabs = [
    { value: "brief", label: "商业事实", icon: Sparkles },
    { value: "evidence", label: "证据来源", icon: Link2, count: sources.length },
    { value: "crm", label: "富通档案", icon: Building2 },
    ...(hasProcess ? [{ value: "process", label: "检索过程", icon: Network }] : []),
  ];
  const activeTab = tabs.some((item) => item.value === tab) ? tab : "brief";
  return (
    <section className="ai-panel">
      <header className="ai-panel-header">
        <div>
          <span><Sparkles size={16} />AI 客户背调</span>
          {inquiry.researched_at ? <small>已生成 · {inquiry.research_search_provider || "公开信息检索"}</small> : <small>客户身份、经营业务与采购机会 · 按证据判断</small>}
        </div>
        <div>
          <Button size="sm" variant="ghost" icon={Settings2} onClick={onOpenTemplates}>模板</Button>
          <Button size="sm" icon={RefreshCw} loading={working} onClick={onGenerate}>{inquiry.ai_research ? "重新背调" : "开始背调"}</Button>
        </div>
      </header>
      {working ? <LoadingState label="正在生成客户背调，请勿重复提交…" compact /> : inquiry.research_error ? (
        <ErrorState title="背调处理失败" message={inquiry.research_error} onRetry={onGenerate} />
      ) : inquiry.ai_research ? (
        <ResearchVerdict inquiry={inquiry} researchDetail={researchDetail} sources={sources} onOpenRating={onOpenRating} />
      ) : null}
      <Tabs items={tabs} value={activeTab} onChange={setTab} label="背调内容分区" />
      {secondaryLoading ? <LoadingState label="正在读取背调证据与置信度…" compact /> : null}
      {secondaryError ? <ErrorState compact title="背调依据暂未加载" message={secondaryError} /> : null}
      <div className="research-tab-body" role="tabpanel">
        {activeTab === "brief" ? (
          inquiry.ai_research && !working ? (
            <>
              <MarkdownText className="research-report research-report--summary">{report.summary}</MarkdownText>
              <ResearchBusinessBrief brief={researchDetail?.business_brief} />
              {!secondaryLoading && researchDetail?.run_id && !researchDetail?.business_brief?.version ? <p className="crm-sop-note">这是一份历史报告。点击“重新背调”可按新版生成客户商业事实；不会改动已发送邮件。</p> : null}
              {report.evidence ? (
                <details className="research-evidence">
                  <summary><FileSearch size={15} />查看 AI 背调依据</summary>
                  <MarkdownText className="research-report research-report--evidence">{report.evidence}</MarkdownText>
                </details>
              ) : null}
            </>
          ) : working || inquiry.research_error ? null : (
            <EmptyState icon={FileSearch} title="尚未生成客户背调" description="系统将结合邮件内容和联网公开来源进行分析，结论仍需人工复核。" action={<Button variant="primary" icon={Sparkles} onClick={onGenerate}>生成背调</Button>} />
          )
        ) : activeTab === "evidence" ? (
          <>
            {(researchDetail?.findings || []).some((finding) => finding.conflict_detected) ? (
              <ResearchClaimList findings={researchDetail.findings.filter((finding) => finding.conflict_detected)} title="存在冲突的结论" />
            ) : null}
            <ResearchSourceList key={inquiry.id} sources={sources} />
          </>
        ) : activeTab === "crm" ? (
          <CrmSopPanel key={inquiry.id} inquiryId={inquiry.id} />
        ) : (
          <ResearchTransparency data={researchDetail} />
        )}
      </div>
      <p className="research-footnote">AI 背调基于公开信息，评分为规则分数而非准确率；未查到信息不代表客户存在风险，关键结论请人工复核。</p>
    </section>
  );
}

function ResearchSourceList({ sources }) {
  const [expanded, setExpanded] = useState(false);
  if (!sources.length) return <EmptyState icon={Link2} title="暂无证据来源" description="完成背调后，这里会汇总本次检索到的公开网页。" />;
  const counts = sources.reduce((result, source) => ({ ...result, [source.stageKey]: (result[source.stageKey] || 0) + 1 }), {});
  const visible = expanded ? sources : sources.slice(0, SOURCE_PREVIEW_LIMIT);
  return (
    <section className="research-source-list" aria-label="证据来源">
      <header>
        <span><strong>证据来源</strong><small>已去重，按核验程度排序；冲突来源排在最前</small></span>
        <div>{["verified", "body_fetched", "found", "legacy", "failed"].filter((key) => counts[key]).map((key) => (
          <Badge key={key} tone={RESEARCH_STAGE_LABELS[key].tone}>{RESEARCH_STAGE_LABELS[key].label} {counts[key]}</Badge>
        ))}</div>
      </header>
      {visible.map((source, index) => {
        const stage = RESEARCH_STAGE_LABELS[source.stageKey];
        const showDiagnosis = ["found", "failed"].includes(source.stageKey) && (source.fetch_diagnosis || source.last_error);
        const meta = [
          source.domain,
          source.source_category ? SOURCE_CATEGORY_LABELS[source.source_category] || source.source_category : "",
          source.trust_level ? TRUST_LEVEL_LABELS[source.trust_level] || source.trust_level : "",
        ].filter(Boolean).join(" · ");
        return (
          <article key={source.id || source.url || index} className={source.conflict_detected ? "has-conflict" : ""}>
            <div>
              <strong>{source.source_name || source.title || source.domain || `来源 ${index + 1}`}</strong>
              {meta ? <small>{meta}</small> : null}
              {source.evidence_excerpt ? <p>{source.evidence_excerpt}</p> : null}
              {showDiagnosis ? <small className="research-source-list__diagnosis">获取说明：{FETCH_DIAGNOSIS_LABELS[source.fetch_diagnosis] || source.last_error}</small> : null}
            </div>
            <footer>
              <Badge tone={stage.tone}>{stage.label}</Badge>
              {source.conflict_detected ? <Badge tone="danger">来源存在冲突</Badge> : null}
              {source.url ? <a href={source.url} target="_blank" rel="noreferrer noopener">打开 <ExternalLink size={12} /></a> : null}
            </footer>
          </article>
        );
      })}
      {sources.length > SOURCE_PREVIEW_LIMIT ? (
        <button type="button" className="research-source-list__more" onClick={() => setExpanded((value) => !value)}>
          {expanded ? "收起" : `显示全部 ${sources.length} 条`}
        </button>
      ) : null}
    </section>
  );
}

function ResearchClaimList({ findings, title }) {
  return <section className="research-claim-list"><header><ShieldCheck size={15} /><strong>{title}</strong><small>同一事实的支持与冲突来源</small></header>{findings.map((finding) => <article key={`${finding.claim_key}-${finding.id}`} className={finding.conflict_detected ? "has-conflict" : ""}><div><small>{CLAIM_LABELS[finding.claim_key] || finding.claim_key}</small><strong>{finding.claim_value || "待补充"}</strong><span>{finding.confidence_score == null ? "证据不足" : `可信度 ${finding.confidence_score}%`} · {finding.independent_source_count || 0} 个独立来源</span></div><div><strong>支持来源</strong>{(finding.supporting_sources || []).map((source, index) => <a key={`${source.id}-${index}`} href={source.url} target="_blank" rel="noreferrer noopener">✓ {source.source_name}：{source.claim_value}</a>)}</div>{finding.conflict_detected ? <div className="research-claim-conflict"><strong>⚠ 来源信息存在冲突，建议人工复核</strong>{(finding.conflicting_sources || []).map((source, index) => <a key={`${source.id}-${index}`} href={source.url} target="_blank" rel="noreferrer noopener">{source.source_name}：{source.claim_value}</a>)}</div> : null}</article>)}</section>;
}

const CONFIDENCE_LABELS = { identity: "身份依据", business: "业务依据", project: "项目依据", contact: "联系人依据", overall: "综合证据" };
const FETCH_DIAGNOSIS_LABELS = {
  tool_extraction_recovered: "原工具提取不足，已补抓",
  tool_extraction_limited: "网页可访问，正文提取不足",
  source_customer_mismatch: "网页可访问，但与客户主体关联不足",
  website_access_restricted: "网站限制访问或需要验证",
  website_not_found: "网页已不存在",
  website_server_error: "网站服务器异常",
  website_unreachable: "网站域名或网络不可达",
  website_non_html_content: "来源不是可读取网页",
  website_redirect_mismatch: "网页跳转到其他网站",
  undetermined_timeout: "访问超时，暂不能区分网站或网络原因",
  undetermined_sparse_page: "页面内容过少，暂不能形成证据",
  undetermined_empty_page: "页面未返回可用正文",
  fallback_service_busy: "补抓服务繁忙，本次已跳过",
  fallback_service_unavailable: "补抓服务暂不可用，不影响主流程",
};

function confidenceValue(confidence, key) {
  const value = confidence?.[key];
  if (value && typeof value === "object") return value.status === "evaluated" ? value.score : null;
  return Number(value) > 0 ? Number(value) : null;
}

function ResearchTransparency({ data }) {
  const confidence = data.confidence || {};
  const overall = confidenceValue(confidence, "overall");
  return <section className="research-transparency" aria-label="检索过程与证据明细">
    <header><ShieldCheck size={15} /><span><strong>检索过程与证据明细</strong><small>实际执行的检索、各维度证据评分与交叉核验</small></span><Badge tone={overall == null ? "warning" : "info"}>{overall == null ? "待补充证据" : `证据评分 ${overall}/100`}</Badge></header>
    <div className="research-transparency__body">
      <section className="research-stage-summary" aria-label="本次背调实际获取阶段">
        {[["searched", "已搜索"], ["found", "找到来源"], ["body_fetched", "取得正文"], ["verified", "核验通过"]].map(([key, label]) => <span key={key}>{label} <b>{data.progress?.[key] || 0}</b></span>)}
        <small>这里统计的是任务步骤数；候选平台不等于已访问。</small>
      </section>
      <section className="research-confidence-grid">{Object.entries(CONFIDENCE_LABELS).map(([key, label]) => { const score = confidenceValue(confidence, key); return <article key={key} className={score == null ? "is-insufficient" : ""}><header><span>{label}</span><strong>{score == null ? "证据不足" : `${score}/100`}</strong></header>{score == null ? <small>待补充独立来源</small> : <i><b style={{ width: `${score}%` }} /></i>}</article>; })}</section>
      <section className="research-plan"><header><Network size={15} /><strong>本次检索计划</strong><small>{data.plan.query_budget || 0} 条查询预算</small></header>{data.plan.tasks.map((task, index) => {
        const progress = data.progress?.steps?.find((item) => item.key === task.type);
        const state = RESEARCH_STAGE_LABELS[progress?.status] || RESEARCH_STAGE_LABELS.pending;
        return <article key={`${task.type}-${index}`}><span><Search size={14} /></span><div><strong>{task.label || task.type}</strong><p>{progress?.reason || task.reason || "根据客户信息自动规划"}</p><small>{progress?.source_count || 0} 个来源 · {progress?.body_count || 0} 个正文</small><small>{(task.queries || []).map((query) => query.query || query).join(" · ")}</small><div className="research-route-sources">{(task.selected_sources || []).map((source, sourceIndex) => <span key={`${source.source_name}-${sourceIndex}`}><b>{source.source_name}</b><em>{SOURCE_CATEGORY_LABELS[source.source_category] || source.source_category} · {TRUST_LEVEL_LABELS[source.trust_level] || source.trust_level} · {source.access_mode === "search_only" ? "仅摘要" : source.access_mode === "manual" ? "需授权查询" : "公开网页"}</em></span>)}</div></div><Badge tone={state.tone}>{state.label}</Badge></article>;
      })}</section>
      {(data.findings || []).length ? <ResearchClaimList findings={data.findings} title="结构化证据结论" /> : null}
    </div>
  </section>;
}
